import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Collapse, Typography } from "@mui/material";
import { type FC, type MouseEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useHandleNurseChange } from "../../hooks/useHandleNurseChange";
import { useHandleRequest } from "../../hooks/useHandleRequest";
import { useLastNameFirst } from "../../hooks/useNameFormat";
import { usePredictCost } from "../../hooks/usePredictCost";
import { useRevealWage } from "../../hooks/useRevealWage";
import { INK, NEUTRAL, STATUS_BADGE, SURFACE } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { buildCostPrediction } from "../../utils/costPrediction";
import { formatPersonFullName } from "../../utils/formatters";
import {
	NotificationType,
	type Nurse,
	type NurseInShift,
	type ReplaceShiftRequest,
	type RequestResponsesStatus,
	type ShiftRequest,
	ShiftType,
} from "../../utils/models";
import { countDailyHours } from "../../utils/statsCalculations";
import { acceptedHoursOf } from "../../utils/utilFunctions";
import { isAgencyNurse, useNurseWarnings, WarningDialog } from "../../utils/warningDialogs/WarningDialog";
import { CustomCalendar } from "../CustomCalendar/CustomCalendar";
import { HeaderButton } from "../headerButton";
import { NurseShiftBadges } from "../NurseShiftBadges/NurseShiftBadges";
import { OvertimeCell } from "../UIToolkit/Cells/OvertimeCell/OvertimeCell";
import { shouldShowOtColumn } from "../UIToolkit/Cells/OvertimeCell/otVisibility";
import { useRequestUtils } from "./Provider/useRequestUtils";

type RequestDataProps = {
	request: ShiftRequest;
	requestData: RequestResponsesStatus;
};

const createNurseInShiftFromRequest = (
	nurse: Nurse,
	request: ShiftRequest,
	requestData: RequestResponsesStatus,
	departmentId: string,
	lastNameFirst: boolean,
): NurseInShift => {
	const { start, end } = acceptedHoursOf(request, requestData);
	return {
		id: "",
		nurseId: nurse.id,
		departmentId,
		startTime: start,
		endTime: end,
		isPendingToDelete: false,
		isPending: false,
		role: nurse.role,
		charge: false,
		name: formatPersonFullName(nurse, lastNameFirst),
		type: nurse.employmentType,
		shiftType: request.message.shift_type,
		notes: "",
		trainingStatus: nurse.trainingStatus,
		bonus: request.current_bonus ?? request.bonus ?? 0,
		breaks: [],
	};
};

export const RequestData: FC<RequestDataProps> = ({ requestData, request }) => {
	const { t } = useTranslation();
	const { refreshRequests } = useRequestsContext();
	const navigate = useNavigate();
	const {
		hospitalData,
		showErrorSnackbar,
		prepareDataForAddNurse,
		getNurseFormattedName,
		getNurseById,
		addNurseToShift,
		removeNurseFromSpecificDate,
		chosenDepartment,
		transformedShifts,
		isMedicalDepartment,
	} = useHospitalData();
	const { isLoading } = useHandleNurseChange();
	const lastNameFirst = useLastNameFirst();
	const { replaceRequest, isLoading: isLoadingRequest } = useHandleRequest();
	const { showSnackbar } = useSnackbar();
	const canViewWage = useRevealWage();
	const { approveAddRequest, isPartialShift } = useRequestUtils();
	const [isExpanded, setIsExpanded] = useState<boolean>(false);

	// Predict shift cost from backend using the nurse's ACCEPTED times — for a partial shift these differ
	// from the posted window, so the card cost matches what approving will actually cost (and the approval
	// warning below reuses this same prediction rather than re-fetching a different one).
	const { mutate: predictCost, data: shiftPrediction, status: predictionStatus } = usePredictCost();
	const { start: predictStart, end: predictEnd } = acceptedHoursOf(request, requestData);
	const canPredict = !!requestData.nurse_id && !!predictStart && !!predictEnd;
	useEffect(() => {
		if (!canPredict) return;
		predictCost({
			departmentId: chosenDepartment.department_id,
			nurseId: requestData.nurse_id,
			startTime: predictStart.toISO() ?? "",
			endTime: predictEnd.toISO() ?? "",
		});
	}, [canPredict, requestData.nurse_id, predictStart, predictEnd, chosenDepartment.department_id, predictCost]);
	// The approval warnings are derived from this prediction, so approving before it settles would silently skip the OT / daily-hours gates.
	const isPredictionUnsettled = canPredict && (predictionStatus === "idle" || predictionStatus === "pending");
	// Unified cost + OT display derived through the same buildCostPrediction path as every other view.
	const cost = buildCostPrediction(shiftPrediction, { isGlobalSalary: Boolean(getNurseById(requestData.nurse_id)?.isGlobalSalary) });

	const [warningState, checkWarnings, handleAgencyConfirm, handleOvertimeConfirm, handleDailyHoursConfirm, handleCancelWarnings] = useNurseWarnings(
		async () => {
			await executeApproveRequest();
		},
		chosenDepartment.longShiftDuration,
	);

	const [pendingNurseId, setPendingNurseId] = useState<number | null>(null);
	const handleApproveClick = async (e: MouseEvent, nurseId: number): Promise<void> => {
		e.stopPropagation();
		setPendingNurseId(nurseId);

		const nurse = getNurseById(nurseId);
		if (!nurse) return;

		// Reuse the card's prediction (already computed on the nurse's accepted times, for this same nurse),
		// so the warning gates on exactly the cost/OT the admin was just shown. dailyHours needs the
		// weekly-hours deltas from that same prediction; guard so a not-yet-loaded prediction still surfaces
		// the warning safely with zeroed hours.
		let dailyHours = 0;
		if (shiftPrediction) {
			const shiftHours = shiftPrediction.weeklyHoursAfter - shiftPrediction.weeklyHoursBefore;
			const { weeklyShifts = [] } = hospitalData.weeks[request.message.weekIndex] || {};
			const originalDailyHours = countDailyHours({
				nurseId: nurse.id,
				departmentId: chosenDepartment.department_id,
				weeklyShifts,
				dayIndex: request.message.dayNo,
				breakRulesConfig: chosenDepartment.breakRulesConfig ?? [],
				nurseUnion: nurse.union,
			});
			dailyHours = originalDailyHours + shiftHours;
		}

		const isAgency = isAgencyNurse(nurse.employmentType);

		checkWarnings(isAgency, cost.overtimeHours ?? 0, cost.costDisplay, dailyHours);
	};

	const executeApproveRequest = async (): Promise<void> => {
		if (!pendingNurseId) return;
		await approveRequest(pendingNurseId);
		setPendingNurseId(null);
	};

	const approveRequest = async (nurseId: number): Promise<void> => {
		trackEvent("approve_request_mobile", {
			request_type: request.request_type,
			nurse_id: nurseId,
		});

		try {
			switch (request.request_type) {
				case NotificationType.OpenShift:
					await approveAddRequest(nurseId, request);
					break;
				case NotificationType.ReplaceShift:
					await approveReplaceRequest(nurseId);
					break;
				default:
					return;
			}
		} catch {
			return;
		}

		await refreshRequests();
		navigate(`/${chosenDepartment.department_id}/staffing/${request.message.shift_start.toFormat("yyyy-MM-dd")}`);
	};

	const approveReplaceRequest = async (nurseId: number): Promise<void> => {
		const shiftNo: number = request.message.shift_type === ShiftType.Day ? 0 : 1;

		let nurseToRemove: NurseInShift | undefined;

		// Backward compatability support
		if ((request as ReplaceShiftRequest).request_for_shift) {
			nurseToRemove = hospitalData.weeks[request.message.weekIndex].weeklyShifts[shiftNo].days[request.message.dayNo].nurses.find(
				(n) => n.id === (request as ReplaceShiftRequest).request_for_shift,
			);
		} else {
			nurseToRemove = hospitalData.weeks[request.message.weekIndex].weeklyShifts[shiftNo].days[request.message.dayNo].nurses.find(
				(n) => n.nurseId === (request as ReplaceShiftRequest).replacement_nurse_id,
			);
		}

		const nurseToAdd = prepareDataForAddNurse(
			hospitalData,
			request.message.weekIndex,
			request.message.dayNo,
			shiftNo,
			nurseId,
			nurseToRemove?.location,
			false,
			request.id,
			undefined,
			undefined,
			nurseToRemove?.bonus,
		);
		nurseToAdd.location = nurseToRemove?.location;

		try {
			await replaceRequest({
				nurseToAdd,
				nurseToRemove,
				weekIndex: request.message.weekIndex,
				dayNo: request.message.dayNo,
				shiftIndex: shiftNo,
				requestId: request?.id,
				date: request.message.date,
			});

			if (nurseToRemove) {
				removeNurseFromSpecificDate(request.message.weekIndex, request.message.dayNo, shiftNo, nurseToRemove.nurseId, false, nurseToRemove.id);
			}
			addNurseToShift(nurseToAdd, request.message.weekIndex, request.message.dayNo, shiftNo);
			showSnackbar(t("success.request.approved"), "success");
		} catch (e) {
			showErrorSnackbar("Failed to approve request", { nurse_id: nurseToAdd.id, request_id: request?.id }, e);
			throw e;
		}
	};

	return (
		<Box
			data-testid={`request-data-${requestData.nurse_id}`}
			sx={{ backgroundColor: SURFACE, padding: 2, marginBottom: 1 }}
			onClick={() => setIsExpanded(!isExpanded)}
		>
			<Box sx={{ display: "flex", justifyContent: "space-between" }}>
				<Typography sx={{ fontWeight: 600 }}>{getNurseFormattedName(requestData.nurse_id)}</Typography>
				<KeyboardArrowDownIcon
					sx={{
						color: INK.MUTED,
						transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
						transition: "transform 0.3s ease",
						fontSize: 20,
					}}
				/>
			</Box>

			{/* Display accepted times if they exist */}
			{requestData.accepted_start_time && requestData.accepted_end_time && (
				<Box
					sx={{
						display: "flex",
						gap: 1.2,
						marginTop: "3px",
						alignItems: "center",
					}}
				>
					<Typography sx={{ fontSize: "14px", color: STATUS_BADGE.WARN_INK, fontWeight: 500 }}>accepted Times:</Typography>
					<Typography sx={{ fontSize: "14px", color: INK.SECONDARY }}>
						{requestData.accepted_start_time.toFormat("h:mm a")} - {requestData.accepted_end_time.toFormat("h:mm a")}
					</Typography>
				</Box>
			)}

			<Box sx={{ display: "flex", gap: 1.2, marginTop: "3px" }}>
				<Typography>{t("predicted.cost")}</Typography>
				{canViewWage && (
					<Typography sx={{ marginLeft: "3px" }}>
						{(() => {
							const displayBonus = request.current_bonus ?? request.bonus;
							// Marginal weekly delta (unified), replacing the old raw shiftCost. Bonus appended per grid convention.
							return isPartialShift(request, requestData.nurse_id) || !displayBonus ? cost.costDisplay : `${cost.costDisplay} (+$${displayBonus})`;
						})()}
					</Typography>
				)}
				<NurseShiftBadges
					shift={createNurseInShiftFromRequest(getNurseById(requestData.nurse_id)!, request, requestData, chosenDepartment.department_id, lastNameFirst)}
					weekIndex={request.message.weekIndex}
				/>
			</Box>

			{shouldShowOtColumn({ hasOvertimeData: cost.overtimeHours != null, isMedicalDepartment }) && (
				<Box sx={{ display: "flex", gap: 1.2, marginTop: "3px", alignItems: "baseline" }}>
					<Typography>{t("nurses.matrix.ot")}</Typography>
					<OvertimeCell
						overtimeHours={cost.overtimeHours ?? 0}
						weeklyOvertimeHoursBeforeChange={cost.weeklyOvertimeHoursBeforeChange}
						showWeeklyOt={cost.weeklyOvertimeHoursBeforeChange != null}
						isAdd
						detailed
						t={t}
					/>
				</Box>
			)}
			<Collapse sx={{ marginTop: "10px" }} in={isExpanded}>
				<Box
					sx={{
						bgcolor: SURFACE,
						borderTop: `1px solid ${NEUTRAL[300]}`,
						position: "relative",
						zIndex: 0,
					}}
				>
					<CustomCalendar value={request.message.shift_start.startOf("day")} isOpen={isExpanded} showOneWeek={true} shifts={transformedShifts} />
					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							marginTop: "10px",
						}}
					>
						<HeaderButton
							data-testid={`request-approve-${request.id}-${requestData.nurse_id}`}
							onPress={(e: MouseEvent) => handleApproveClick(e, requestData.nurse_id)}
							state="on"
							text={t("approve.request")}
							icon={<CheckIcon style={{ fontSize: "20px" }} />}
							disabled={isLoading.addNurse || isLoadingRequest.replaceRequest || isPredictionUnsettled}
							width="150px"
							height="30px"
							secondary={false}
						/>
					</Box>
				</Box>
			</Collapse>

			<WarningDialog
				openAgencyWarning={warningState.isAgencyWarningOpen}
				openOvertimeWarning={warningState.isOvertimeWarningOpen}
				openDailyHoursWarning={warningState.isDailyHoursWarningOpen}
				overtimeDetails={warningState.overtimeDetails}
				dailyHoursDetails={warningState.dailyHoursDetails}
				longShiftDuration={chosenDepartment.longShiftDuration}
				onCancelAgency={handleCancelWarnings}
				onConfirmAgency={handleAgencyConfirm}
				onCancelOvertime={handleCancelWarnings}
				onConfirmOvertime={handleOvertimeConfirm}
				onCancelDailyHours={handleCancelWarnings}
				onConfirmDailyHours={handleDailyHoursConfirm}
			/>
		</Box>
	);
};
